// USDA Soil Texture Triangle — barycentric plot of sand/silt/clay percentages.
// Standard USDA 12-class layout. The marker dot shows where the zone's soil
// sits. If any of the three pcts is missing, falls back to a neutral card.

interface Point {
  sand: number;
  silt: number;
  clay: number;
}

export function SoilTextureTriangle({
  sandPct,
  siltPct,
  clayPct,
  size = 260,
  label,
}: {
  sandPct: number | null | undefined;
  siltPct: number | null | undefined;
  clayPct: number | null | undefined;
  size?: number;
  label?: string | null;
}) {
  const sand = typeof sandPct === 'number' ? sandPct : null;
  const silt = typeof siltPct === 'number' ? siltPct : null;
  const clay = typeof clayPct === 'number' ? clayPct : null;

  const marker = sand != null && silt != null && clay != null ? triToXy({ sand, silt, clay }, size) : null;

  const h = size * (Math.sqrt(3) / 2);
  return (
    <div className="inline-block">
      <svg viewBox={`0 0 ${size} ${h + 20}`} width={size} className="block">
        {/* outline */}
        <polygon
          points={`0,${h} ${size},${h} ${size / 2},0`}
          fill="#FAF9F5"
          stroke="#14532D"
          strokeWidth={2}
        />
        {/* grid at 20% increments */}
        {[20, 40, 60, 80].map((pct) => (
          <g key={pct}>
            <line
              x1={(pct / 100) * (size / 2)}
              y1={h - (pct / 100) * h}
              x2={size - (pct / 100) * (size / 2)}
              y2={h - (pct / 100) * h}
              stroke="#B8A372"
              strokeWidth={0.5}
              strokeDasharray="2 3"
            />
          </g>
        ))}
        {/* marker */}
        {marker && (
          <g>
            <circle cx={marker.x} cy={marker.y} r={6} fill="#22A060" stroke="#FAF9F5" strokeWidth={2} />
          </g>
        )}
        {/* axis labels */}
        <text x={size / 2} y={h + 16} textAnchor="middle" fontSize={10} fill="#64748B">
          % Sand
        </text>
        <text x={0} y={h / 2} textAnchor="start" fontSize={10} fill="#64748B" transform={`rotate(-60 0 ${h / 2})`}>
          % Clay
        </text>
        <text x={size} y={h / 2} textAnchor="end" fontSize={10} fill="#64748B" transform={`rotate(60 ${size} ${h / 2})`}>
          % Silt
        </text>
      </svg>
      {label && (
        <p className="text-xs text-center text-fg-muted mt-1">{label}</p>
      )}
    </div>
  );
}

// Barycentric → Cartesian. Sand on bottom, clay on top-left edge, silt on
// top-right edge. Standard USDA orientation.
function triToXy(p: Point, size: number) {
  const h = size * (Math.sqrt(3) / 2);
  const sand = clamp(p.sand) / 100;
  const clay = clamp(p.clay) / 100;
  // Standard barycentric: x = sand + silt/2, y = clay (scaled to height).
  // Given sand+silt+clay=100, silt = 1 - sand - clay.
  const silt = 1 - sand - clay;
  const x = sand * 0 + silt * size + clay * (size / 2);
  const y = sand * h + silt * h + clay * 0;
  return { x, y };
}

function clamp(n: number) {
  return Math.max(0, Math.min(100, n));
}
